/**
 * Meta Conversions API (CAPI) Utility
 * 
 * This module handles server-side event tracking to Meta's Conversions API
 * for more reliable and privacy-compliant event tracking.
 */

// Meta Conversions API Configuration
const PIXEL_ID = '4279616155642469';
const ACCESS_TOKEN = 'EACPYcIZBMk0ABQp68IFRxarhsyw6F1Azse9H7I2ZAv86simU9wDDbZCHpNIz4UjQpCkwlr4B89EZCntPNQkPeds0MBhGLHmrDzA2IoZC8x8dSCJvk317yv1b7x5Q6ldDpWW4j6eKLmqBsZBsqpmmq56vsF4D91FFyimjSIqqQDtQeo0ufBS0nxEX2C6grAVPRRGQZDZD';
const API_VERSION = 'v24.0';
const API_URL = `https://graph.facebook.com/${API_VERSION}/${PIXEL_ID}/events`;

/**
 * Hash a string using SHA-256
 * @param {string} value - Value to hash
 * @returns {Promise<string>} Hashed value in hex format
 */
async function sha256Hash(value) {
  if (!value) return null;
  
  // Normalize the value (lowercase, trim whitespace)
  const normalized = value.toLowerCase().trim();
  
  // Convert string to array buffer
  const msgBuffer = new TextEncoder().encode(normalized);
  
  // Hash the message
  const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
  
  // Convert to hex string
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  
  return hashHex;
}

/**
 * Get Facebook click ID (fbc) from URL or cookie
 * @returns {string|null} Facebook click ID
 */
function getFbc() {
  try {
    // Check URL parameter first
    const urlParams = new URLSearchParams(window.location.search);
    const fbclid = urlParams.get('fbclid');
    
    if (fbclid) {
      // Format: fb.1.[subdomain_index].[creation_time].[fbclid]
      const timestamp = Math.floor(Date.now() / 1000);
      return `fb.1.${timestamp}.${fbclid}`;
    }
    
    // Check cookie
    const fbcCookie = document.cookie
      .split('; ')
      .find(row => row.startsWith('_fbc='));
    
    if (fbcCookie) {
      return fbcCookie.split('=')[1];
    }
  } catch (error) {
    console.error('Error getting fbc:', error);
  }
  
  return null;
}

/**
 * Get Facebook browser ID (fbp) from cookie
 * @returns {string|null} Facebook browser ID
 */
function getFbp() {
  try {
    const fbpCookie = document.cookie
      .split('; ')
      .find(row => row.startsWith('_fbp='));
    
    if (fbpCookie) {
      return fbpCookie.split('=')[1];
    }
  } catch (error) {
    console.error('Error getting fbp:', error);
  }
  
  return null;
}

/**
 * Get client IP address (approximate)
 * Note: This will be the user's IP, actual IP detection requires server-side
 * @returns {string|null} Client IP address
 */
function getClientIpAddress() {
  // This is a placeholder - actual IP detection requires server-side implementation
  // For now, we'll let Meta detect it from the request
  return null;
}

/**
 * Send event to Meta Conversions API
 * @param {Object} eventData - Event data to send
 * @returns {Promise<Object>} API response
 */
async function sendConversionsAPIEvent(eventData) {
  try {
    const {
      eventName,
      eventId,
      email,
      phone,
      firstName,
      lastName,
      city,
      customData = {},
    } = eventData;

    // Generate event time (current timestamp in seconds)
    const eventTime = Math.floor(Date.now() / 1000);

    // Hash user data
    const hashedEmail = email ? await sha256Hash(email) : null;
    const hashedPhone = phone ? await sha256Hash(phone.replace(/\D/g, '')) : null;
    const hashedFirstName = firstName ? await sha256Hash(firstName) : null;
    const hashedLastName = lastName ? await sha256Hash(lastName) : null;
    const hashedCity = city ? await sha256Hash(city) : null;

    // Build user_data object
    const userData = {
      client_user_agent: navigator.userAgent,
      fbc: getFbc(),
      fbp: getFbp(),
    };

    // Add hashed user data
    if (hashedEmail) userData.em = [hashedEmail];
    if (hashedPhone) userData.ph = [hashedPhone];
    if (hashedFirstName) userData.fn = [hashedFirstName];
    if (hashedLastName) userData.ln = [hashedLastName];
    if (hashedCity) userData.ct = [hashedCity];

    // Build the event object
    const event = {
      event_name: eventName,
      event_time: eventTime,
      event_source_url: window.location.href,
      action_source: 'website',
      user_data: userData,
    };

    // Add event ID for deduplication (same ID used in browser pixel)
    if (eventId) {
      event.event_id = eventId;
    }

    // Add custom data if provided
    if (Object.keys(customData).length > 0) {
      event.custom_data = customData;
    }

    // Prepare the request payload
    const payload = {
      data: [event],
      access_token: ACCESS_TOKEN,
    };

    // Send the event to Meta Conversions API
    const response = await fetch(API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(`CAPI Error: ${result.error?.message || 'Unknown error'}`);
    }

    console.log('✅ CAPI Event sent successfully:', eventName, result);
    return result;

  } catch (error) {
    console.error('❌ Error sending CAPI event:', error);
    // Don't throw - we don't want to break the user experience
    return null;
  }
}

/**
 * Track Lead event via Conversions API
 * @param {Object} formData - Form submission data
 * @param {string} mode - 'application' or 'download'
 * @param {string} eventId - Event ID for deduplication (should match browser pixel event)
 * @returns {Promise<Object>} API response
 */
export async function trackCAPILead(formData, mode = 'application', eventId = null) {
  // Use provided eventId or generate one if not provided
  const finalEventId = eventId || `lead_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  
  const customData = {
    content_name: mode === 'application' ? formData.programme : 'Course Details Download',
    content_category: mode === 'application' ? 'Acting Programme Application' : 'Course Brochure',
  };

  // Add monetary value for applications
  if (mode === 'application' && formData.programme) {
    if (formData.programme.includes('Diploma')) {
      customData.value = 236000;
      customData.currency = 'INR';
    } else if (formData.programme.includes('Weekend')) {
      customData.value = 94400;
      customData.currency = 'INR';
    }
  }

  // Parse full name into first and last name
  const nameParts = formData.fullName ? formData.fullName.trim().split(' ') : [];
  const firstName = nameParts[0] || '';
  const lastName = nameParts.slice(1).join(' ') || '';

  return sendConversionsAPIEvent({
    eventName: 'Lead',
    eventId: finalEventId,
    email: formData.email,
    phone: formData.phone,
    firstName: firstName,
    lastName: lastName,
    city: formData.city,
    customData: customData,
  });
}

/**
 * Track CompleteRegistration event via Conversions API
 * @param {string} mode - 'application' or 'download'
 * @param {Object} userData - User data from session/URL params if available
 * @param {string} eventId - Event ID for deduplication (should match browser pixel event)
 * @returns {Promise<Object>} API response
 */
export async function trackCAPICompleteRegistration(mode = 'application', userData = {}, eventId = null) {
  // Use provided eventId or generate one if not provided
  const finalEventId = eventId || `registration_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  
  const customData = {
    content_name: mode === 'application' ? 'Acting Programme Application' : 'Course Details Download',
    status: 'completed',
  };

  return sendConversionsAPIEvent({
    eventName: 'CompleteRegistration',
    eventId: finalEventId,
    email: userData.email,
    phone: userData.phone,
    firstName: userData.firstName,
    lastName: userData.lastName,
    city: userData.city,
    customData: customData,
  });
}

/**
 * Track Contact event via Conversions API
 * @param {string} contactMethod - 'WhatsApp', 'Phone', or 'Email'
 * @param {string} eventId - Event ID for deduplication (should match browser pixel event)
 * @returns {Promise<Object>} API response
 */
export async function trackCAPIContact(contactMethod, eventId = null) {
  // Use provided eventId or generate one if not provided
  const finalEventId = eventId || `contact_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  
  const customData = {
    content_name: contactMethod,
    content_category: 'Footer Contact',
  };

  return sendConversionsAPIEvent({
    eventName: 'Contact',
    eventId: finalEventId,
    customData: customData,
  });
}

/**
 * Track InitiateCheckout event via Conversions API
 * @param {string} mode - 'application' or 'download'
 * @param {string} eventId - Event ID for deduplication (should match browser pixel event)
 * @returns {Promise<Object>} API response
 */
export async function trackCAPIInitiateCheckout(mode = 'application', eventId = null) {
  // Use provided eventId or generate one if not provided
  const finalEventId = eventId || `checkout_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  
  const customData = {
    content_name: mode === 'application' ? 'Acting Programme Application' : 'Course Details Download',
    content_category: 'Application Form',
  };

  return sendConversionsAPIEvent({
    eventName: 'InitiateCheckout',
    eventId: finalEventId,
    customData: customData,
  });
}

/**
 * Track ViewContent event via Conversions API
 * @param {string} contentName - Name of the content viewed
 * @param {Array} contentIds - IDs of content items
 * @param {string} eventId - Event ID for deduplication (should match browser pixel event)
 * @returns {Promise<Object>} API response
 */
export async function trackCAPIViewContent(contentName, contentIds = [], eventId = null) {
  // Use provided eventId or generate one if not provided
  const finalEventId = eventId || `view_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  
  const customData = {
    content_name: contentName,
    content_category: 'Acting Programmes',
    content_ids: contentIds,
  };

  return sendConversionsAPIEvent({
    eventName: 'ViewContent',
    eventId: finalEventId,
    customData: customData,
  });
}
